import { useMutation } from '@apollo/client';
import {
  Button,
  Form,
  Input,
  Modal,
  type ModalProps,
  Tabs,
  type TabsProps,
} from 'antd';
import { isEmpty } from 'lodash';
import { useContext, useEffect, useState } from 'react';
import { AppContext } from '../../../../AppContext';
import {
  MAX_COMMENT_CATEGORY_CHAR_LENGTH,
  SHOULD_GO_TO_NEXT_PAGE_AFTER_FEEDBACK_SUBMIT,
} from '../../../../common/constants';
import FeedbackVoteFormItem from '../../../../components/FeedbackVoteFormItem';
import LoaderComponent from '../../../../components/LoaderComponent';
import useAutoFocusInput from '../../../../hooks/use-auto-focus-input';
import type { AppContextType } from '../../../../types/appContext.type';
import { VoteEnum } from '../../../../types/common.type';
import { CREATE_FEEDBACK } from '../../graphql/mutations';
import RatingSummaryForm from '../components/SI/RatingSummaryForm';
import MarkdownViewer from './MarkdownViewer';
import type { SexuallyInappropriateAnalysesData } from './SexuallyInappropriateAnalyses';
import CostTokenDetails from './SI/CostTokenDetails';
import SignalDetectionForm, {
  type SignalDetectionFormValues,
} from './SI/SignalDetectionForm';
import SuggestiveContentForm, {
  type SuggestiveContentFormValues,
} from './SI/SuggestiveContentForm';

const { TextArea } = Input;

type SubSectionDetailsModalProps = {
  modalOpen: boolean;
  closeModal: () => void;
  selectSexuallyInappropriateData:
    | SexuallyInappropriateAnalysesData
    | undefined;
  setActiveIndex: React.Dispatch<React.SetStateAction<number>>;
  activeIndex: number;
  loading?: boolean;
  changePage: (page: number) => void;
  pagination: {
    page: number;
    pageSize: number;
    total: number;
    listDataCount: number;
  };
  modifySexuallyInappropriateData: (
    id: string,
    value: {
      sexuallyFirstPersonPerspective?: boolean | null;
      sexuallyPhysicalContactType?: string[] | null;
      sexuallySetting?: string[] | null;
      sexuallyExplicitTermsFound?: boolean | null;
      sexuallyExplicitTermsList?: string[] | null;
      sexuallyAnatomicalTermsPresent?: boolean | null;
      sexuallyAnatomicalTermsList?: string[] | null;
      sexuallyContainsDialogue?: boolean | null;
      sexuallyChromosomallyDefinedGender?: string[] | null;
      sexuallySelfIdentifiedGenders?: string[] | null;
      sexualRelationshipType?: string[];
      characterAge?: string[];
      euphemismsList?: string[];
      suggestivePhrasesList?: string[];
      powerImbalancePresent?: boolean | null;
      tone?: string[];
      excessiveFocusOnBodyDescription?: boolean | null;
      languageOfSecrecyOrConcealment?: boolean | null;
      roleplayOrFantasyContext?: boolean | null;
      voilenceOverlap?: boolean | null;
      nudityDetected?: string[];
      nudityExtent?: string[];
      nudityType?: string[];
      sexualActsDetected?: string[];
      sexuallyClientComments?: string;
      sexuallyUpvote?: boolean;
      sexuallyDownvote?: boolean;
      sexuallySexualRelationshipType?: string[];
      sexuallyCharacterAge?: string[];
      sexuallyEuphemismsList?: string[];
      sexuallySuggestivePhrasesList?: string[];
      sexuallyPowerImbalancePresent?: boolean | null;
      sexuallyTone?: string[];
      sexuallyExcessiveFocusOnBodyDescription?: boolean | null;
      sexuallyLanguageOfSecrecyOrConcealment?: boolean | null;
      sexuallyRoleplayOrFantasyContext?: boolean | null;
      sexuallyViolenceOverlap?: boolean | null;
      sexuallyNudityDetected?: string[];
      sexuallyNudityExtent?: string[];
      sexuallyNudityType?: string[];
      sexuallySexualActsDetected?: string[];
      sexuallyFinalRating?: number | null;
      sexuallyRatingReason?: string | null;
    },
  ) => void;
} & ModalProps;

type FormType = {
  vote?: VoteEnum;
  sexuallyClientComments?: string;
  sexuallyFirstPersonPerspective?: boolean | null;
  sexuallyPhysicalContactType?: string[] | null;
  sexuallySetting?: string[] | null;
  sexuallyExplicitTermsFound?: boolean | null;
  sexuallyExplicitTermsList?: string[] | null;
  sexuallyAnatomicalTermsPresent?: boolean | null;
  sexuallyAnatomicalTermsList?: string[] | null;
  sexuallyContainsDialogue?: boolean | null;
  sexuallyChromosomallyDefinedGender?: string[] | null;
  sexuallySelfIdentifiedGenders?: string[] | null;
  sexuallySexualRelationshipType?: string[];
  sexuallyCharacterAge?: string[];
  sexuallyEuphemismsList?: string[];
  sexuallySuggestivePhrasesList?: string[];
  sexuallyPowerImbalancePresent?: boolean | null;
  sexuallyTone?: string[];
  sexuallyExcessiveFocusOnBodyDescription?: boolean | null;
  sexuallyLanguageOfSecrecyOrConcealment?: boolean | null;
  sexuallyRoleplayOrFantasyContext?: boolean | null;
  sexuallyViolenceOverlap?: boolean | null;
  sexuallyNudityDetected?: string[];
  sexuallyNudityExtent?: string[];
  sexuallyNudityType?: string[];
  sexuallySexualActsDetected?: string[];
  sexuallyFinalRating?: number | null;
  sexuallyRatingReason?: string | null;
};

type RatingSummaryFormValues = {
  vote?: VoteEnum;
  sexuallyClientComments?: string;
  sexuallyFirstPersonPerspective?: boolean | null;
  sexuallyPhysicalContactType?: string[] | null;
  sexuallySetting?: string[] | null;
  sexuallyExplicitTermsFound?: boolean | null;
  sexuallyExplicitTermsList?: string[] | null;
  sexuallyAnatomicalTermsPresent?: boolean | null;
  sexuallyAnatomicalTermsList?: string[] | null;
  sexuallyContainsDialogue?: boolean | null;
  sexuallyChromosomallyDefinedGender?: string[] | null;
  sexuallySelfIdentifiedGenders?: string[] | null;
  sexuallySexualRelationshipType?: string[];
  sexuallyCharacterAge?: string[];
  sexuallyEuphemismsList?: string[];
  sexuallySuggestivePhrasesList?: string[];
  sexuallyPowerImbalancePresent?: boolean | null;
  sexuallyTone?: string[];
  sexuallyExcessiveFocusOnBodyDescription?: boolean | null;
  sexuallyLanguageOfSecrecyOrConcealment?: boolean | null;
  sexuallyRoleplayOrFantasyContext?: boolean | null;
  sexuallyViolenceOverlap?: boolean | null;
  sexuallyNudityDetected?: string[];
  sexuallyNudityExtent?: string[];
  sexuallyNudityType?: string[];
  sexuallySexualActsDetected?: string[];
  sexuallyFinalRating?: number | null;
  sexuallyRatingReason?: string | null;
};

const SexuallyInappropriateDetailsModal: React.FC<
  SubSectionDetailsModalProps
> = (props) => {
  const {
    modalOpen,
    closeModal,
    selectSexuallyInappropriateData,
    setActiveIndex,
    activeIndex,
    loading: dataLoading = false,
    pagination,
    changePage,
    modifySexuallyInappropriateData,
  } = props;

  const {
    state: { currentUser },
  } = useContext(AppContext) as AppContextType;

  const [activeTab, setActiveTab] = useState<
    'signalDetection' | 'suggestiveContentDetection' | 'ratingSummary'
  >('signalDetection');
  const [submitBtnDisabled, setIsSubmitBtnDisabled] = useState(true);
  const [form] = Form.useForm<FormType>();
  const vote = Form.useWatch('vote', form) ?? VoteEnum.NoVote;
  const inputRef = useAutoFocusInput(vote === VoteEnum.DownVote);
  const comment = Form.useWatch('sexuallyClientComments', form) ?? 0;
  const feedbackData = selectSexuallyInappropriateData?.subSection?.feedback;

  const isNextPage = () => {
    const dataCount = pagination.listDataCount;
    form.resetFields();
    setIsSubmitBtnDisabled(true);
    const isLastItemOnPage = dataCount === activeIndex + 1;
    if (isLastItemOnPage) {
      if (
        dataCount < pagination?.pageSize ||
        dataCount * pagination?.page === pagination?.total
      ) {
        closeModal();
        setActiveTab('signalDetection');
      } else {
        setActiveTab('signalDetection');
        changePage(pagination?.page + 1);
        setActiveIndex(0);
      }
      return;
    }
    setActiveTab('signalDetection');
    setActiveIndex((prev) => prev + 1);
  };

  const [createFeedback, { loading }] = useMutation(CREATE_FEEDBACK, {
    onCompleted() {
      if (SHOULD_GO_TO_NEXT_PAGE_AFTER_FEEDBACK_SUBMIT) isNextPage();
    },
    onError() {},
  });

  const [signalDetectionFormInstance] =
    Form.useForm<SignalDetectionFormValues>();

  const [suggestiveContentFormInstance] =
    Form.useForm<SuggestiveContentFormValues>();

  const [ratingSummaryFormInstance] = Form.useForm<RatingSummaryFormValues>();

  const onFinishCombined = async ({
    sexuallyFirstPersonPerspective,
    sexuallyPhysicalContactType,
    sexuallySetting,
    sexuallyExplicitTermsFound,
    sexuallyExplicitTermsList,
    sexuallyAnatomicalTermsPresent,
    sexuallyAnatomicalTermsList,
    sexuallyContainsDialogue,
    sexuallyChromosomallyDefinedGender,
    sexuallySelfIdentifiedGenders,
    sexuallyClientComments,
    vote,
    sexuallySexualRelationshipType,
    sexuallyCharacterAge,
    sexuallyEuphemismsList,
    sexuallySuggestivePhrasesList,
    sexuallyPowerImbalancePresent,
    sexuallyTone,
    sexuallyExcessiveFocusOnBodyDescription,
    sexuallyLanguageOfSecrecyOrConcealment,
    sexuallyRoleplayOrFantasyContext,
    sexuallyViolenceOverlap,
    sexuallyNudityDetected,
    sexuallyNudityExtent,
    sexuallyNudityType,
    sexuallySexualActsDetected,
    sexuallyFinalRating,
    sexuallyRatingReason,
  }: FormType | SignalDetectionFormValues | SuggestiveContentFormValues) => {
    const updateFields:
      | FormType
      | SignalDetectionFormValues
      | SuggestiveContentFormValues = {
      sexuallyFirstPersonPerspective,
      sexuallyPhysicalContactType: sexuallyPhysicalContactType?.map(
        (val) => val.split('__')[0] ?? '',
      ),
      sexuallySetting: sexuallySetting?.map((val) => val.split('__')[0] ?? ''),
      sexuallyExplicitTermsFound,
      sexuallyExplicitTermsList: sexuallyExplicitTermsList?.map(
        (val) => val.split('__')[0] ?? '',
      ),
      sexuallyAnatomicalTermsPresent,
      sexuallyAnatomicalTermsList: sexuallyAnatomicalTermsList?.map(
        (val) => val.split('__')[0] ?? '',
      ),
      sexuallyContainsDialogue,
      sexuallyChromosomallyDefinedGender:
        sexuallyChromosomallyDefinedGender?.map(
          (val) => val.split('__')[0] ?? '',
        ),
      sexuallySelfIdentifiedGenders: sexuallySelfIdentifiedGenders?.map(
        (val) => val.split('__')[0] ?? '',
      ),
      sexuallyClientComments,
      sexuallySexualRelationshipType: sexuallySexualRelationshipType?.map(
        (val) => val.split('__')[0] ?? '',
      ),
      sexuallyCharacterAge: sexuallyCharacterAge?.map(
        (val) => val.split('__')[0] ?? '',
      ),
      sexuallyEuphemismsList: sexuallyEuphemismsList?.map(
        (val) => val.split('__')[0] ?? '',
      ),
      sexuallySuggestivePhrasesList: sexuallySuggestivePhrasesList?.map(
        (val) => val.split('__')[0] ?? '',
      ),
      sexuallyPowerImbalancePresent,
      sexuallyTone: sexuallyTone?.map((val) => val.split('__')[0] ?? ''),
      sexuallyExcessiveFocusOnBodyDescription,
      sexuallyLanguageOfSecrecyOrConcealment,
      sexuallyRoleplayOrFantasyContext,
      sexuallyViolenceOverlap,
      sexuallyNudityDetected: sexuallyNudityDetected?.map(
        (val) => val.split('__')[0] ?? '',
      ),
      sexuallyNudityExtent: sexuallyNudityExtent?.map(
        (val) => val.split('__')[0] ?? '',
      ),
      sexuallyNudityType: sexuallyNudityType?.map(
        (val) => val.split('__')[0] ?? '',
      ),
      sexuallySexualActsDetected: sexuallySexualActsDetected?.map(
        (val) => val.split('__')[0] ?? '',
      ),
      sexuallyFinalRating,
      sexuallyRatingReason,
    };

    const { data } = await createFeedback({
      variables: {
        data: {
          ...updateFields,
          userId: currentUser?.id,
          subSection: selectSexuallyInappropriateData?.subSection?.documentId,
          sexuallyDownvote: vote === VoteEnum.DownVote,
          sexuallyUpvote: vote === VoteEnum.UpVote,
          sexuallyClientComments: sexuallyClientComments ?? '',
        },
      },
    });

    if (data?.createFeedback) {
      modifySexuallyInappropriateData(
        selectSexuallyInappropriateData?.documentId ?? '',
        {
          ...updateFields,
          sexuallyDownvote: vote === VoteEnum.DownVote,
          sexuallyUpvote: vote === VoteEnum.UpVote,
          sexuallyClientComments: sexuallyClientComments ?? '',
        },
      );
    }
  };

  const items: TabsProps['items'] = [
    {
      key: 'signalDetection',
      label: 'Signal Detection',
      children: (
        <SignalDetectionForm
          form={signalDetectionFormInstance}
          setIsSubmitBtnDisabled={setIsSubmitBtnDisabled}
          selectSexuallyInappropriateData={selectSexuallyInappropriateData}
        />
      ),
    },
    {
      key: 'suggestiveContentDetection',
      label: 'Suggestive Content Detection',
      children: (
        <SuggestiveContentForm
          form={suggestiveContentFormInstance}
          setIsSubmitBtnDisabled={setIsSubmitBtnDisabled}
          selectSexuallyInappropriateData={selectSexuallyInappropriateData}
        />
      ),
    },
    {
      key: 'ratingSummary',
      label: 'Rating + Summarization',
      children: (
        <RatingSummaryForm
          form={ratingSummaryFormInstance}
          setIsSubmitBtnDisabled={setIsSubmitBtnDisabled}
          selectSexuallyInappropriateData={selectSexuallyInappropriateData}
        />
      ),
    },
    {
      key: 'costTokens',
      label: 'Cost & Tokens',
      children: (
        <CostTokenDetails
          selectSexuallyInappropriateData={selectSexuallyInappropriateData}
        />
      ),
    },
  ];

  const handleTabChange = (key: string) => {
    const newTab = key as
      | 'signalDetection'
      | 'suggestiveContentDetection'
      | 'ratingSummary';
    setActiveTab(newTab);
  };

  useEffect(() => {
    signalDetectionFormInstance?.setFieldsValue({
      sexuallyFirstPersonPerspective:
        feedbackData?.sexuallyFirstPersonPerspective ??
        selectSexuallyInappropriateData?.firstPersonPerspective,
      sexuallyPhysicalContactType:
        feedbackData?.sexuallyPhysicalContactType ??
        selectSexuallyInappropriateData?.physicalContactType,
      sexuallySetting:
        feedbackData?.sexuallySetting ??
        selectSexuallyInappropriateData?.setting,
      sexuallyExplicitTermsFound:
        feedbackData?.sexuallyExplicitTermsFound ??
        selectSexuallyInappropriateData?.explicitTermsFound,
      sexuallyExplicitTermsList:
        feedbackData?.sexuallyExplicitTermsList ??
        selectSexuallyInappropriateData?.explicitTermsList,
      sexuallyAnatomicalTermsPresent:
        feedbackData?.sexuallyAnatomicalTermsPresent ??
        selectSexuallyInappropriateData?.anatomicalTermsPresent,
      sexuallyAnatomicalTermsList:
        feedbackData?.sexuallyAnatomicalTermsList ??
        selectSexuallyInappropriateData?.anatomicalTermsList,
      sexuallyContainsDialogue:
        feedbackData?.sexuallyContainsDialogue ??
        selectSexuallyInappropriateData?.containsDialogue,
      sexuallySelfIdentifiedGenders:
        feedbackData?.sexuallySelfIdentifiedGenders ??
        selectSexuallyInappropriateData?.selfIdentifiedGenders,
      sexuallyChromosomallyDefinedGender:
        feedbackData?.sexuallyChromosomallyDefinedGender ??
        selectSexuallyInappropriateData?.chromosomallyDefinedGender,
    });
    suggestiveContentFormInstance?.setFieldsValue({
      sexuallySexualRelationshipType:
        feedbackData?.sexuallySexualRelationshipType ??
        selectSexuallyInappropriateData?.sexualRelationshipType,
      sexuallyCharacterAge:
        feedbackData?.sexuallyCharacterAge ??
        selectSexuallyInappropriateData?.characterAge,
      sexuallyEuphemismsList:
        feedbackData?.sexuallyEuphemismsList ??
        selectSexuallyInappropriateData?.euphemismsList,
      sexuallySuggestivePhrasesList:
        feedbackData?.sexuallySuggestivePhrasesList ??
        selectSexuallyInappropriateData?.suggestivePhrasesList,
      sexuallyPowerImbalancePresent:
        feedbackData?.sexuallyPowerImbalancePresent ??
        selectSexuallyInappropriateData?.powerImbalancePresent,
      sexuallyTone:
        feedbackData?.sexuallyTone ?? selectSexuallyInappropriateData?.tone,
      sexuallyExcessiveFocusOnBodyDescription:
        feedbackData?.sexuallyExcessiveFocusOnBodyDescription ??
        selectSexuallyInappropriateData?.excessiveFocusOnBodyDescription,
      sexuallyLanguageOfSecrecyOrConcealment:
        feedbackData?.sexuallyLanguageOfSecrecyOrConcealment ??
        selectSexuallyInappropriateData?.languageOfSecrecyOrConcealment,
      sexuallyRoleplayOrFantasyContext:
        feedbackData?.sexuallyRoleplayOrFantasyContext ??
        selectSexuallyInappropriateData?.roleplayOrFantasyContext,
      sexuallyViolenceOverlap:
        feedbackData?.sexuallyViolenceOverlap ??
        selectSexuallyInappropriateData?.violenceOverlap,
      sexuallyNudityDetected:
        feedbackData?.sexuallyNudityDetected ??
        selectSexuallyInappropriateData?.nudityDetected,
      sexuallyNudityExtent:
        feedbackData?.sexuallyNudityExtent ??
        selectSexuallyInappropriateData?.nudityExtent,
      sexuallyNudityType:
        feedbackData?.sexuallyNudityType ??
        selectSexuallyInappropriateData?.nudityType,
      sexuallySexualActsDetected:
        feedbackData?.sexuallySexualActsDetected ??
        selectSexuallyInappropriateData?.sexualActsDetected,
    });
    form.setFieldsValue({
      vote: feedbackData?.sexuallyUpvote
        ? VoteEnum.UpVote
        : feedbackData?.sexuallyDownvote
          ? VoteEnum.DownVote
          : VoteEnum.NoVote,
      sexuallyClientComments:
        !isEmpty(feedbackData?.sexuallyClientComments) &&
        feedbackData?.sexuallyClientComments !== null
          ? feedbackData?.sexuallyClientComments
          : `I expected ... \n I experienced ...`,
    });
    ratingSummaryFormInstance.setFieldsValue({
      sexuallyFinalRating:
        feedbackData?.sexuallyFinalRating ??
        selectSexuallyInappropriateData?.finalRating,
      sexuallyRatingReason:
        feedbackData?.sexuallyRatingReason ??
        selectSexuallyInappropriateData?.ratingReason,
    });
  }, [selectSexuallyInappropriateData, activeIndex]);

  const handleSubmitBothForms = async () => {
    const signalDetectionValues =
      await signalDetectionFormInstance.validateFields();
    const commentFormValues = await form.validateFields();

    const suggestiveContentValues =
      await suggestiveContentFormInstance.validateFields();

    const ratingSummaryValues =
      await ratingSummaryFormInstance.validateFields();

    onFinishCombined({
      ...signalDetectionValues,
      ...commentFormValues,
      ...suggestiveContentValues,
      ...ratingSummaryValues,
    });
  };

  return (
    <Modal
      open={modalOpen}
      onCancel={() => {
        closeModal();
        setActiveTab('signalDetection');
      }}
      centered
      onOk={() => setActiveIndex((prev) => prev + 1)}
      title={
        <div className="d-flex align-center section-modal title">
          <h5>Sexually Inappropriate Analysis Report</h5>
          <div className="section-modal content">
            <span>
              <strong>Final Rating</strong>:{' '}
              <span className="content-values">
                {selectSexuallyInappropriateData?.finalRating}
              </span>
            </span>
            <span>
              <strong>Page #</strong>:{' '}
              <span className="content-values">
                {selectSexuallyInappropriateData?.subSection?.pageNumber}
              </span>
            </span>
            <span>
              <strong>Para #</strong>:{' '}
              <span className="content-values">
                {selectSexuallyInappropriateData?.subSection?.paragraphNumber}
              </span>
            </span>
          </div>
        </div>
      }
      width="auto"
      okText="Next"
      footer={null}
      cancelText="Previous"
      className="sub-section-modal"
      {...props}
    >
      {dataLoading ? (
        <LoaderComponent />
      ) : (
        <div className="modal-book-wrapper">
          <div className="modal-book-wrapper-image p-0 paragraph-section">
            <fieldset>
              <legend>Input (Paragraph)</legend>
              <MarkdownViewer
                content={
                  selectSexuallyInappropriateData?.subSection?.paragraph ?? ''
                }
                className="modal-markdown-scroll"
              />
            </fieldset>
          </div>
          <div className="modal-book-wrapper-content content-section content-section-flex">
            <div>
              <Tabs
                items={items.map((item) => ({
                  ...item,
                  children: (
                    <div className="modal-tab-content-scroll-section">
                      {item.children}
                    </div>
                  ),
                }))}
                activeKey={activeTab}
                defaultActiveKey="1"
                onChange={handleTabChange}
              />
            </div>
            <div className="modal-category-section category-section-fixed">
              {/*
              // commenting for future use
              <div className="d-flex mt-16 full-width">
                <Table<SexuallyInappropriateAnalysesData>
                  columns={columns}
                  dataSource={dataSource}
                  pagination={false}
                  scroll={{
                    x: 'max-content',
                  }}
                />
              </div> */}
              <Form
                layout="horizontal"
                form={form}
                className="mt-8"
                onValuesChange={() => setIsSubmitBtnDisabled(false)}
                initialValues={{
                  vote: VoteEnum.NoVote,
                  suggestedRating: null,
                  suggestedCategory: null,
                  sexuallyClientComments: `I expected ... \n I experienced ...`,
                }}
              >
                <FeedbackVoteFormItem
                  vote={vote}
                  name="vote"
                  onChange={(_vote) => {
                    const isUpvote = _vote === VoteEnum.UpVote;

                    form.setFieldValue(
                      'sexuallyClientComments',
                      isUpvote === feedbackData?.sexuallyUpvote
                        ? !isEmpty(feedbackData?.sexuallyClientComments) &&
                          feedbackData?.sexuallyClientComments !== null
                          ? feedbackData?.sexuallyClientComments
                          : `I expected ... \n I experienced ...`
                        : `I expected ... \n I experienced ...`,
                    );
                    form.setFieldValue('vote', _vote);
                  }}
                  label="Feedback"
                />
                {vote !== VoteEnum.NoVote && (
                  <Form.Item
                    name="sexuallyClientComments"
                    rules={[
                      {
                        max: MAX_COMMENT_CATEGORY_CHAR_LENGTH,
                        message: `Comment cannot exceed ${MAX_COMMENT_CATEGORY_CHAR_LENGTH} characters`,
                      },
                    ]}
                  >
                    <TextArea className="feedback-textarea" ref={inputRef} />
                  </Form.Item>
                )}
                <Form.Item>
                  <Button
                    disabled={
                      loading ||
                      submitBtnDisabled ||
                      (vote &&
                        vote === VoteEnum.DownVote &&
                        (comment ?? '') === '')
                    }
                    onClick={handleSubmitBothForms}
                    loading={loading}
                  >
                    Submit
                  </Button>
                </Form.Item>
              </Form>
              <div className="d-flex gap-8 justify-end">
                <Button
                  onClick={() => {
                    if (activeIndex === 0) {
                      changePage(pagination?.page - 1);
                      form.resetFields();
                      setActiveIndex(pagination.pageSize - 1);
                      setIsSubmitBtnDisabled(true);
                      return;
                    }
                    setActiveIndex((prev) => prev - 1);
                    form.resetFields();
                  }}
                  disabled={
                    loading || (pagination.page === 1 && activeIndex === 0)
                  }
                >
                  Previous
                </Button>
                <Button
                  onClick={() => {
                    isNextPage();
                  }}
                  disabled={loading}
                >
                  Next
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </Modal>
  );
};

export default SexuallyInappropriateDetailsModal;
